import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  TextField,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { API_URL } from '../Config';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import '../common/commonStyles.css';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const StockReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [salescategory, setSalescategory] = useState('');
  const [salesbrand, setSalesbrand] = useState('');
  const [data, setData] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    totalBoxQty: '0.00',
    totalPiecesQty: '0.00',
    totalAmount: '0.00'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [filteredCategoryOptions, setFilteredCategoryOptions] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandInputValue, setBrandInputValue] = useState('');
  const [filteredBrandOptions, setFilteredBrandOptions] = useState([]);

  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    const formattedStartDate = value[0] ? format(value[0], 'dd/MM/yyyy') : '';
    const formattedEndDate = value[1] ? format(value[1], 'dd/MM/yyyy') : '';
    setReportrange(formattedStartDate ? `${formattedStartDate} - ${formattedEndDate}` : '');
    setPage(0);
  };

  const fetchCategoriesOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/stock_take/getCategories`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await res.json();
      const formattedOptions = data.data.category.map(item => ({ value: item.id, label: item.name }));
      setCategoryOptions(formattedOptions);
      setFilteredCategoryOptions(formattedOptions);
    } catch (err) {
    }
  };

  const fetchBrandOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/stock_take/getBrands`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await res.json();
      const formattedOptions = data.data.brands.map(item => ({ value: item.id, label: item.name }));
      setBrandOptions(formattedOptions);
      setFilteredBrandOptions(formattedOptions);
    } catch (err) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: String(page + 1),
        start: String(page * rowsPerPage),
        length: String(rowsPerPage),
        searchValue,
        reportrange,
        brand_id: salesbrand,
        category_id: salescategory
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/stockReport`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.data.length);

        setFooterTotals({
          totalBoxQty: parseFloat(json.BoxQuantity || 0).toFixed(2),
          totalPiecesQty: parseFloat(json.PiecesQuantity || 0).toFixed(2),
          totalAmount: (() => {
            let totalAmount = 0;
            if (json.total !== undefined && json.total !== null && json.total !== '') {
              const cleaned = json.total.toString().replace(/,/g, '');
              totalAmount = parseFloat(cleaned);
              if (isNaN(totalAmount)) totalAmount = 0;
            }
            return totalAmount.toFixed(2);
          })()
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchValue, salesbrand, salescategory]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchCategoriesOptions();
    fetchBrandOptions();
  }, [accessToken]);

  useEffect(() => {
    if (reportrange) {
      const timer = setTimeout(() => {
        fetchReport();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fetchReport]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPage(0);
      fetchReport();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: '1',
      start: '0',
      length: totalCount,
      reportrange,
      brand_id: salesbrand,
      category_id: salescategory
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const res = await fetch(`${config.API_URL}/stockReport`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: formData
    });

    const raw = await res.json();
    const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

    const exportData = data.data.map((product, index) => ({
      "SR": index + 1,
      "Code": product.product_code || '',
      "Name": product.product_name || '',
      "Category": product.categories || '',
      "Brand": product.brand || '',
      "Cost": product.cost || 0,
      "Pack": product.pack || 0,
      "Total Box Qty": product.BoxQuantity || 0,
      "Total Piece Qty": product.PiecesQuantity || 0,
      "Total Amount": parseFloat(product.total || 0).toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');
    XLSX.writeFile(wb, 'Sales_Product_Report.xlsx');
  };

  const handleCategoryInputChange = (event, newInputValue) => {
    setCategoryInputValue(newInputValue);
    const filtered = categoryOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredCategoryOptions(filtered);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setSalescategory(newValue ? newValue.value : '');
  };

  const handleBrandInputChange = (event, newInputValue) => {
    setBrandInputValue(newInputValue);
    const filtered = brandOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredBrandOptions(filtered);
  };

  const handleBrandChange = (event, newValue) => {
    setSelectedBrand(newValue);
    setSalesbrand(newValue ? newValue.value : '');
  };

  const handleSubmit = () => {
    setPage(0);
    fetchReport();
  };

  return (
    <>
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Typography className="page-title" mb={1}>
              Stock Report
            </Typography>

            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} alignContent="end">
                <TextField
                  fullWidth
                  className="input-border"
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: '#0e0f0fef' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                  style={{ width: '100%' }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                <Autocomplete
                  className="input-border"
                  size="small"
                  options={filteredCategoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  inputValue={categoryInputValue}
                  onInputChange={handleCategoryInputChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Category"
                      label="Category"
                      className="text-field input-border"
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                <Autocomplete
                  className="input-border"
                  size="small"
                  options={filteredBrandOptions}
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  inputValue={brandInputValue}
                  onInputChange={handleBrandInputChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select Brand" label="Brand" 
                     className="text-field input-border"
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 2 }} alignContent="end">
                <Stack direction="row" spacing={1}>
                  <Button
                    className="button-variant-contained"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    fullWidth
                  >
                    Submit
                  </Button>
                  <Button
                    className="button-variant-outlined"
                    variant="outlined"
                    fullWidth
                    onClick={exportToExcel}
                    startIcon={<Iconify icon="eva:download-outline" width={18} />}
                  >
                    Export
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer
          >
            <Table size="medium" layout="fixed" className="table-cell-border">
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{width: 80 }}>Sr. No.</TableCell>
                  <TableCell sx={{width: 100 }}>Code</TableCell>
                  <TableCell sx={{ width: 300 }}>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Pack</TableCell>
                  <TableCell align="right">Total Box Qty</TableCell>
                  <TableCell align="right" sx={{width: 100 }}>Total Piece Qty</TableCell>
                  <TableCell align="right" sx={{width: 100 }}>Total Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : data.length > 0 ? (
                  data.map((row, index) => (
                    <TableRow key={index} hover className="table-cell-style" sx={{
                      '& .MuiTableCell-root': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }
                    }}>
                      <TableCell sx={{textAlign: 'center' }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell >{row.product_code || ''}</TableCell>
                      <TableCell title={row.product_name}>{row.product_name || ''}</TableCell>
                      <TableCell>{row.categories || ''}</TableCell>
                      <TableCell>{row.brand || ''}</TableCell>
                      <TableCell align="right">{row.cost || 0}</TableCell>
                      <TableCell align="right">{row.pack || 0}</TableCell>
                      <TableCell align="right">{row.BoxQuantity || 0}</TableCell>
                      <TableCell align="right">{row.PiecesQuantity || 0}</TableCell>
                      <TableCell align="right">{parseFloat(row.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow 
                  className="table-cell-style"
                  sx={{
                    backgroundColor: '#f5f7fa',
                    '& .MuiTableCell-root': {
                      fontWeight: 600                
                    }
                  }}
                >
                  <TableCell colSpan={7} align="right">
                    Total:
                  </TableCell>
                  <TableCell align="right">
                    {footerTotals.totalBoxQty}
                  </TableCell>
                  <TableCell align="right">
                    {footerTotals.totalPiecesQty}
                  </TableCell>
                  <TableCell align="right">
                    {footerTotals.totalAmount}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            className="pagination-styles"
          />
        </Paper>
      </Box>
    </>
  );
};

export default StockReport;
